<template>
    <div
        class="bloco bloco-indicador"
        :class="[`tema-${tema}`, { 'bloco-aberto': aberto }]"
    >
        <button
            type="button"
            class="tituloBloco"
            :aria-expanded="aberto"
            @click="aberto = !aberto"
        >
            <span class="titulo-esquerda">
                <span class="area-badge" aria-hidden="true">
                    <i :class="iconeArea"></i>
                </span>
                <span class="titulo-textos">
                    <strong>{{ titulo }}</strong>
                    <small v-if="descricao" class="bloco-descricao">{{ descricao }}</small>
                </span>
            </span>
            <span class="titulo-direita">
                <span v-if="subtitulo" class="bloco-subtitulo">{{ subtitulo }}</span>
                <i class="caret" :class="iconeCaret" aria-hidden="true"></i>
            </span>
        </button>
        <div v-if="aberto" class="bloco-conteudo">
            <slot />
        </div>
    </div>
</template>

<script>
const ICONES = {
    estrategico: 'bi bi-bullseye',
    comercial: 'bi bi-briefcase',
    financeiro: 'bi bi-cash-coin',
    producao: 'bi bi-gear-wide-connected',
};

export default {
    name: 'BlocoAccordion',
    props: {
        titulo: { type: String, required: true },
        tema: {
            type: String,
            default: 'comercial',
            validator: (v) => ['estrategico', 'comercial', 'financeiro', 'producao'].includes(v),
        },
        subtitulo: { type: String, default: '' },
        descricao: { type: String, default: '' },
        abertoInicial: { type: Boolean, default: false },
    },
    data() {
        return {
            aberto: this.abertoInicial,
        };
    },
    computed: {
        iconeCaret() {
            return this.aberto ? 'bi bi-chevron-up' : 'bi bi-chevron-down';
        },
        iconeArea() {
            return ICONES[this.tema] || ICONES.comercial;
        },
    },
};
</script>

<style scoped>
.bloco-indicador {
    overflow: hidden;
    box-shadow: 0 1px 18px var(--cor-cinza);
    transition: box-shadow 0.2s ease;
}

.bloco-aberto {
    box-shadow: 0 4px 24px var(--cor-cinza);
}

.tituloBloco {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;
    padding: 1rem 1.15rem;
    border: none;
    border-bottom: 3px solid var(--accent);
    border-radius: 0;
    cursor: pointer;
    text-align: left;
    background: var(--header-bg);
    color: inherit;
    transition: background-color 0.15s ease;
}

.tituloBloco:hover {
    background: var(--header-bg-hover);
}

.titulo-esquerda {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    min-width: 0;
}

.area-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 12px;
    flex-shrink: 0;
    background: var(--accent);
    color: #fff;
    box-shadow: 0 2px 8px color-mix(in srgb, var(--accent) 35%, transparent);
}

.area-badge i {
    font-size: 1.15rem;
    line-height: 1;
    color: #fff;
}

.titulo-textos {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
}

.titulo-textos strong {
    font-size: 1.15rem;
    font-weight: 700;
    line-height: 1.2;
    color: var(--title-color);
}

.bloco-descricao {
    font-size: 0.8rem;
    font-weight: 400;
    line-height: 1.35;
    color: var(--desc-color);
}

.titulo-direita {
    display: inline-flex;
    align-items: center;
    gap: 0.65rem;
    flex-shrink: 0;
}

.bloco-subtitulo {
    padding: 0.28rem 0.7rem;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    background: var(--accent);
    color: #fff;
}

.caret {
    font-size: 1rem;
    color: var(--accent);
    opacity: 0.85;
}

.bloco-conteudo {
    background: var(--cor-bg);
}

.tema-estrategico {
    --accent: #5b4fcf;
    --header-bg: #f3f1fb;
    --header-bg-hover: #ebe8f8;
    --title-color: #3d348a;
    --desc-color: #6b6694;
}

.tema-comercial {
    --accent: #3571cd;
    --header-bg: #eef4fc;
    --header-bg-hover: #e4eef9;
    --title-color: #1f457d;
    --desc-color: #5a6f8c;
}

.tema-financeiro {
    --accent: #2e9e6b;
    --header-bg: #eef8f3;
    --header-bg-hover: #e3f3eb;
    --title-color: #1c6344;
    --desc-color: #4f7a65;
}

.tema-producao {
    --accent: #e13417;
    --header-bg: #fdf0ed;
    --header-bg-hover: #fae6e1;
    --title-color: #8f2412;
    --desc-color: #8a5a50;
}

.dark .tema-estrategico,
html.dark .tema-estrategico {
    --header-bg: rgba(91, 79, 207, 0.18);
    --header-bg-hover: rgba(91, 79, 207, 0.28);
    --title-color: #cfc8ff;
    --desc-color: rgba(255, 255, 255, 0.65);
}

.dark .tema-comercial,
html.dark .tema-comercial {
    --header-bg: rgba(53, 113, 205, 0.18);
    --header-bg-hover: rgba(53, 113, 205, 0.28);
    --title-color: #b7d0f5;
    --desc-color: rgba(255, 255, 255, 0.65);
}

.dark .tema-financeiro,
html.dark .tema-financeiro {
    --header-bg: rgba(46, 158, 107, 0.18);
    --header-bg-hover: rgba(46, 158, 107, 0.28);
    --title-color: #a8e0c4;
    --desc-color: rgba(255, 255, 255, 0.65);
}

.dark .tema-producao,
html.dark .tema-producao {
    --header-bg: rgba(225, 52, 23, 0.18);
    --header-bg-hover: rgba(225, 52, 23, 0.28);
    --title-color: #ffb3a4;
    --desc-color: rgba(255, 255, 255, 0.65);
}
</style>
